/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  Alert, Container, InputAdornment, IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { confirmSignIn, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';

type Step =
  | 'credentials'
  | 'new_password'
  | 'totp_setup'
  | 'totp_verify'
  | 'forgot_password'    // enter email to request reset code
  | 'reset_confirm';     // enter code + new password

export default function Login() {
  const [step, setStep] = useState<Step>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetNewPasswordConfirm, setResetNewPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, confirmLogin, checkAuthStatus } = useAuth();
  const navigate = useNavigate();

  // ── Step 1: email + password ──────────────────────────────────────────────
  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(username, password);
      if (result.success) {
        navigate('/');
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setStep('new_password');
      } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        startTotpSetup(result.nextStep);
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setStep('totp_verify');
      } else if (result.error) {
        setError(result.error);
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2a: set new password ─────────────────────────────────────────────
  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== newPasswordConfirm) { setError('Passwords do not match'); return; }
    if (newPassword.length < 12) { setError('Password must be at least 12 characters'); return; }
    setLoading(true);
    try {
      const result = await confirmLogin(newPassword);
      if (result.success) {
        navigate('/');
      } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        startTotpSetup(result.nextStep);
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setStep('totp_verify');
      } else {
        setError(result.error || 'Failed to set new password');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  // ── TOTP setup — get the URI from the nextStep details ───────────────────
  const startTotpSetup = (nextStep: any) => {
    try {
      const uri = nextStep.totpSetupDetails.getSetupUri('LibRide Admin', username).toString();
      setTotpUri(uri);
      setStep('totp_setup');
    } catch (err: any) {
      setError(err.message || 'Failed to start TOTP setup');
    }
  };

  // ── Step 2b: confirm TOTP setup with first code ───────────────────────────
  const handleTotpSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // confirmSignIn handles both verifying the TOTP setup and completing sign-in
      const result = await confirmLogin(totpCode);
      if (result.success) {
        navigate('/');
      } else {
        setError(result.error || 'Invalid code — check your authenticator app');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2c: verify TOTP on subsequent logins ─────────────────────────────
  const handleTotpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await confirmLogin(totpCode);
      if (result.success) {
        navigate('/');
      } else {
        setError(result.error || 'Invalid code');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  // ── Step: forgot password — send reset code ───────────────────────────────
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword({ username: resetEmail });
      setStep('reset_confirm');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  // ── Step: confirm reset — code + new password ─────────────────────────────
  const handleResetConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (resetNewPassword !== resetNewPasswordConfirm) { setError('Passwords do not match'); return; }
    if (resetNewPassword.length < 12) { setError('Password must be at least 12 characters'); return; }
    setLoading(true);
    try {
      await confirmResetPassword({ username: resetEmail, confirmationCode: resetCode, newPassword: resetNewPassword });
      setStep('credentials');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card sx={{ width: '100%', maxWidth: 420 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">Admin Login</Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Liberia Ride-Hailing Platform
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* ── Credentials ── */}
            {step === 'credentials' && (
              <form onSubmit={handleCredentials}>
                <TextField
                  fullWidth label="Email" type="email" variant="outlined" margin="normal"
                  value={username} onChange={e => setUsername(e.target.value)}
                  required autoFocus autoComplete="username"
                />
                <TextField
                  fullWidth label="Password" variant="outlined" margin="normal"
                  type={showPassword ? 'text' : 'password'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  required autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button fullWidth type="submit" variant="contained" size="large" disabled={loading} sx={{ mt: 3 }}>
                  {loading ? 'Signing in…' : 'Sign In'}
                </Button>
                <Button fullWidth variant="text" size="small"
                  onClick={() => { setResetEmail(username); setStep('forgot_password'); }}
                  sx={{ mt: 1 }}>
                  Forgot password?
                </Button>
              </form>
            )}

            {/* ── New password required ── */}
            {step === 'new_password' && (
              <form onSubmit={handleNewPassword}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  First login — please set a permanent password.
                </Alert>
                <TextField
                  fullWidth label="New password" variant="outlined" margin="normal"
                  type={showNew ? 'text' : 'password'}
                  value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  required autoFocus autoComplete="new-password"
                  helperText="Min 12 chars · upper + lower + number + symbol"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNew(v => !v)} edge="end">
                          {showNew ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth label="Confirm new password" variant="outlined" margin="normal"
                  type="password" value={newPasswordConfirm}
                  onChange={e => setNewPasswordConfirm(e.target.value)}
                  required autoComplete="new-password"
                />
                <Button fullWidth type="submit" variant="contained" size="large" disabled={loading} sx={{ mt: 3 }}>
                  {loading ? 'Setting password…' : 'Set Password & Continue'}
                </Button>
                <Button fullWidth variant="text" onClick={() => setStep('credentials')} sx={{ mt: 1 }}>
                  Back
                </Button>
              </form>
            )}

            {/* ── TOTP setup (first time) ── */}
            {step === 'totp_setup' && (
              <form onSubmit={handleTotpSetup}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.), then enter the 6-digit code to confirm.
                </Alert>
                {totpUri && (
                  <Box display="flex" justifyContent="center" mb={2}>
                    {/* Render QR via a free API — no external dependency needed */}
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(totpUri)}`}
                      alt="TOTP QR code"
                      width={180}
                      height={180}
                    />
                  </Box>
                )}
                <TextField
                  fullWidth label="6-digit code" variant="outlined" margin="normal"
                  value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required autoFocus inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  helperText="Enter the code shown in your authenticator app"
                />
                <Button fullWidth type="submit" variant="contained" size="large"
                  disabled={loading || totpCode.length !== 6} sx={{ mt: 3 }}>
                  {loading ? 'Verifying…' : 'Confirm & Sign In'}
                </Button>
              </form>
            )}

            {/* ── TOTP verify (subsequent logins) ── */}
            {step === 'totp_verify' && (
              <form onSubmit={handleTotpVerify}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Enter the 6-digit code from your authenticator app.
                </Typography>
                <TextField
                  fullWidth label="Authenticator code" variant="outlined" margin="normal"
                  value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required autoFocus inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                />
                <Button fullWidth type="submit" variant="contained" size="large"
                  disabled={loading || totpCode.length !== 6} sx={{ mt: 3 }}>
                  {loading ? 'Verifying…' : 'Verify'}
                </Button>
                <Button fullWidth variant="text" onClick={() => setStep('credentials')} sx={{ mt: 1 }}>
                  Back
                </Button>
              </form>
            )}

            {/* ── Forgot password — request code ── */}
            {step === 'forgot_password' && (
              <form onSubmit={handleForgotPassword}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Enter your email and we'll send a reset code.
                </Typography>
                <TextField
                  fullWidth label="Email" type="email" variant="outlined" margin="normal"
                  value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                  required autoFocus autoComplete="username"
                />
                <Button fullWidth type="submit" variant="contained" size="large" disabled={loading} sx={{ mt: 3 }}>
                  {loading ? 'Sending…' : 'Send Reset Code'}
                </Button>
                <Button fullWidth variant="text" onClick={() => setStep('credentials')} sx={{ mt: 1 }}>
                  Back to Login
                </Button>
              </form>
            )}

            {/* ── Reset confirm — code + new password ── */}
            {step === 'reset_confirm' && (
              <form onSubmit={handleResetConfirm}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  A reset code was sent to {resetEmail}. Enter it below with your new password.
                </Alert>
                <TextField
                  fullWidth label="Reset code" variant="outlined" margin="normal"
                  value={resetCode} onChange={e => setResetCode(e.target.value.trim())}
                  required autoFocus inputProps={{ inputMode: 'numeric' }}
                />
                <TextField
                  fullWidth label="New password" type="password" variant="outlined" margin="normal"
                  value={resetNewPassword} onChange={e => setResetNewPassword(e.target.value)}
                  required autoComplete="new-password"
                  helperText="Min 12 chars · upper + lower + number + symbol"
                />
                <TextField
                  fullWidth label="Confirm new password" type="password" variant="outlined" margin="normal"
                  value={resetNewPasswordConfirm} onChange={e => setResetNewPasswordConfirm(e.target.value)}
                  required autoComplete="new-password"
                />
                <Button fullWidth type="submit" variant="contained" size="large" disabled={loading} sx={{ mt: 3 }}>
                  {loading ? 'Resetting…' : 'Reset Password'}
                </Button>
                <Button fullWidth variant="text" onClick={() => setStep('forgot_password')} sx={{ mt: 1 }}>
                  Resend code
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
